import type { CSSProperties } from 'react'

import { staggerDelayStyle } from '@/lib/motion/stagger'

/**
 * How one element enters: `fade-up` for a section or block arriving, `fade-in`
 * for words landing in place, `scale-in` for a tile, a badge or a mark.
 */
export type RevealVariant = 'fade-up' | 'fade-in' | 'scale-in'

const ENTRANCES: Record<RevealVariant, string> = {
  'fade-up': 'animate-fade-up',
  'fade-in': 'animate-fade-in',
  'scale-in': 'animate-scale-in',
}

export interface RevealTiming {
  /** Its stagger step: 40ms each, capped at 320ms. */
  index?: number | undefined
  /**
   * A delay of its own, as a CSS time, for an entrance timed off something
   * other than its place in a list: a chart's dot landing as its line reaches
   * it. Wins over `index`.
   */
  delay?: string | undefined
  /**
   * True once it has played. Drops the entrance, so a library that remounts
   * the element later (recharts redrawing its shapes for new figures) cannot
   * replay it.
   */
  entered?: boolean | undefined
}

export interface EntranceProps {
  className: string | undefined
  style: CSSProperties | undefined
}

/**
 * The class and inline delay `Reveal` puts on its element, for the rare one
 * it cannot wrap: a router `Link` tile, whose props a polymorphic wrapper
 * would stop type-checking.
 */
export function revealMotion(
  variant: RevealVariant,
  { index, delay, entered = false }: RevealTiming = {},
): EntranceProps {
  if (entered) return { className: undefined, style: undefined }
  const style =
    delay !== undefined
      ? { animationDelay: delay }
      : index === undefined
        ? undefined
        : staggerDelayStyle(index)
  return { className: ENTRANCES[variant], style }
}
